import { type ButtonHTMLAttributes, type ReactNode } from 'react';
import { LoadingSpinner } from './LoadingSpinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'game3d' | 'gameNav';
  size?: 'sm' | 'md' | 'lg' | 'square';
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles =
    'inline-flex items-center justify-center gap-1.5 font-semibold rounded-xl border border-transparent transition-all duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-900 active:translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-y-0';

  const variants = {
    primary:
      'bg-gradient-to-br from-game-blue via-[#2694ff] to-game-blue-dark text-white border-white/10 shadow-[0_12px_24px_-16px_rgba(0,123,255,0.95)] hover:-translate-y-0.5 hover:shadow-[0_18px_30px_-18px_rgba(0,123,255,1)] hover:saturate-110 focus-visible:ring-game-blue/70',
    secondary:
      'bg-white/85 dark:bg-neutral-800/85 backdrop-blur-md text-text-dark dark:text-neutral-200 border-surface-border/90 dark:border-neutral-600/80 shadow-[0_8px_22px_-18px_rgba(15,23,42,0.55)] hover:-translate-y-0.5 hover:bg-white dark:hover:bg-neutral-800 hover:border-game-blue/40 hover:shadow-[0_14px_26px_-20px_rgba(0,123,255,0.55)] focus-visible:ring-game-blue/60',
    ghost:
      'bg-transparent text-text-muted dark:text-neutral-400 border-transparent hover:-translate-y-0.5 hover:text-text-dark dark:hover:text-neutral-100 hover:bg-surface-secondary/85 dark:hover:bg-neutral-800/85 hover:border-surface-border dark:hover:border-neutral-700 focus-visible:ring-game-blue/50',
    game3d:
      'btn-game-3d bg-game-blue text-white font-bold rounded-[8px] focus:ring-game-blue',
    gameNav:
      'btn-game-nav bg-white dark:bg-neutral-800 text-game-blue-dark dark:text-game-blue font-bold border-2 border-game-blue focus:ring-game-blue',
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm h-8',
    md: 'px-4 py-2 text-sm h-10',
    lg: 'px-6 py-3 text-base h-12',
    square: 'w-14 h-14 text-sm',
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <LoadingSpinner size="sm" className="mr-2" />
      ) : leftIcon ? (
        <span className="mr-2">{leftIcon}</span>
      ) : null}
      {children}
      {rightIcon && !isLoading && <span className="ml-2">{rightIcon}</span>}
    </button>
  );
}
